import { Grid } from "@trussworks/react-uswds";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useContext, useState } from "react";
//import { POPULATIONS_SERVED, TypeOfHelp } from "../../../../types";
import { 
  SearchFilters,
 // clearHourFilters,
 } from "../../../../types/v1_schemas";

import DesktopControlDropdown from "./DesktopControlDropdown";
import FeePreferenceInput from "../FeePreferenceInput";
import HoursInput from "../HoursInput";
import LanguageInput from "../LanguageInput";
import TypeOfHelpInput from "../TypeOfHelpInput";
import AccessibilityInput from "../AccessibilityInput";
import DistanceInput from "../DistanceInput";
import ControlToggles from "./DesktopControlToggles";
import AgeGroupInput from "../AgeGroupInput";
import TelehealthInput from "../TelehealthInput";
import { SurveyTriggerContext } from "../../../../App";
import PopulationsServedInput from "../PopulationsServedInput";
import { SEARCH_FILTER_ORDER } from "../../../../pages/Search/Search";

/*
(alias) const SEARCH_FILTER_ORDER: readonly 
["TypeOfHelp", "Distance", "Age", "Populations", "Language", "Fees", "Hours", "Accessibility", "Telehealth"]
import SEARCH_FILTER_ORDER
*/

type DesktopControlProps = {
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
  distanceUpdatedExternally: boolean;
  typeOfHelpIsOpen: boolean;
  setTypeOfHelpIsOpen: Dispatch<SetStateAction<boolean>>;
};

function DesktopControl({
  filters,
  setFilters,
  distanceUpdatedExternally,
  typeOfHelpIsOpen,
  setTypeOfHelpIsOpen,
}: DesktopControlProps) {
  const { t } = useTranslation();
  // Global application state to track trigger events for showing user
  // option to take site survey
  const { incrementTriggerEventCount } = useContext(SurveyTriggerContext);
  const setFiltersAndIncrement: Dispatch<SetStateAction<SearchFilters>> = (
    _filters
  ) => {
    incrementTriggerEventCount();
    setFilters(_filters);
  };

  // flag to display distance filter in active state ONLY
  // after user has explicitly changed from default value (zipcode search)
  // or original selection (guided search)
  const [showDistanceActive, setShowDistanceActive] = useState(false);

  return (
    <div className="display-none tablet:display-block print-hide" style={{"width":"auto"}}>
      <Grid row className="margin-bottom-2" style={{"width":"130%"}}>
        <div className="usa-sr-only nice_font">{t("filterBy")}</div>
        {SEARCH_FILTER_ORDER.map((filter:any) => {
          if (filter === "Accessibility") {
            return (
              <DesktopControlDropdown
                title={t("accessibilityTitle")}
                hasSelection={!!filters.accessibilitySupportIds?.length}
                clear={() =>
                  setFiltersAndIncrement({ ...filters, accessibilitySupportIds: [] })
                }
                key="accessibilityTitle"
              >
                <AccessibilityInput
                  legend={t("accessibilityTitle")}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "Age") {
            return (
              <DesktopControlDropdown
                title={t("ageTitle")}
                hasSelection={!!filters.patientAgeGroupIds?.length}
                clear={() => {
                  // pluck age out of filters
                  const { patientAgeGroupIds, ...updatedFilters } = filters;
                  setFiltersAndIncrement(updatedFilters as SearchFilters);
                }}
                key="Age"
              >
                <AgeGroupInput
                  legend={t("ageTitle")}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "Distance") {
            return (
              <DesktopControlDropdown
                title={t("distanceTitle")}
                key={"Distance"}
                hasSelection={showDistanceActive || distanceUpdatedExternally}                
              >
                <DistanceInput
                  legend={t("distanceTitle")}
                  compact
                  filters={filters}
                  setFilters={(_filters) => {
                    setShowDistanceActive(true);
                    setFiltersAndIncrement(_filters);
                  }}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "Fees") {
            return (
              <DesktopControlDropdown
                title={t("feesTitle")}
                hasSelection={!!filters.paymentTypeIds?.length}
                clear={() => 
                  setFilters({ ...filters, paymentTypeIds: [] })}
                key="Fees"
              >
                <FeePreferenceInput
                  legend={t("feesTitle")}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "Hours") {
            return (
              <DesktopControlDropdown
                title={t("hoursTitle")}
                key="Hours"
                hasSelection={!!filters.openSunday?.length 
                  || !!filters.openMonday?.length
                  || !!filters.openTuesday?.length
                  || !!filters.openWednesday?.length
                  || !!filters.openThursday?.length
                  || !!filters.openFriday?.length
                  || !!filters.openSaturday?.length
                }
                clear={
                  () => {
                    let f = filters;
                    delete f.openMonday;
                    delete f.openTuesday;
                    delete f.openWednesday;
                    delete f.openThursday;
                    delete f.openFriday;
                    delete f.openSaturday;
                    delete f.openSunday;
                    setFilters(f);
                  }
                }
              >
                <HoursInput
                  compact
                  options={["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday", "Sunday"]}
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "Language") {
            return (
              <DesktopControlDropdown
                title={t("languageTitle")}
                hasSelection={!!filters.languageIds?.length}
                clear={() =>
                  setFiltersAndIncrement({ ...filters, languageIds: [] })
                }
                key="Language"
              >
                <LanguageInput
                  legend={t("languageTitle")}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "Telehealth") {
            return (
              <DesktopControlDropdown
                title={t("telehealthTitle")}
                hasSelection={!!filters.telehealthOptions?.length}
                clear={() =>
                  setFiltersAndIncrement({
                    ...filters,
                    telehealthOptions: [],
                  })
                }
                key="Telehealth"
              >
                <TelehealthInput
                  legend={t("telehealthTitle")}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }
          if (filter === "TypeOfHelp") {
            return (
              <DesktopControlDropdown
                title={t("typeOfHelpTitle")}
                hasSelection={!!filters.serviceProvidedIds?.length}
                clear={() =>
                  setFiltersAndIncrement({ ...filters, serviceProvidedIds: [] })
                }
                key="TypeOfHelp"
              >
                <TypeOfHelpInput
                  legend={t("typeOfHelpTitle")}
                  isMobile={false}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
                <a onClick={() => setTypeOfHelpIsOpen(!typeOfHelpIsOpen)} style={{textDecoration:"underline", cursor:"pointer"}}>{t("showMoreServices")}</a>
              </DesktopControlDropdown>
            );
          }
          if (filter === "Populations") {
            return (
              <DesktopControlDropdown
                title={t("populationsServedTitle")}
                hasSelection={!!filters.patientCommunityIds?.length}
                clear={() =>
                  setFiltersAndIncrement({
                    ...filters,
                    patientCommunityIds: [],
                  })
                }
                key="Populations"
              >
                <PopulationsServedInput
                  legend={t("populationsServedTitle")}
                  compact
                  filters={filters}
                  setFilters={setFiltersAndIncrement}
                  key={filter}
                  />
              </DesktopControlDropdown>
            );
          }

          return <></>;
        })}
      </Grid>
      <ControlToggles setFilters={setFiltersAndIncrement} />
    </div>
  );
}

export default DesktopControl;
